import React, { useCallback, useEffect, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import FocusLock from "react-focus-lock";
import { Link, useLocation, useParams, useRouteMatch } from "react-router-dom";
import useQueryParams from "../../common/useQueryParams";
import IconTimes from "../../icons/IconTimes";
import IconCloudCMA from "../../icons/logos/horizontal/IconCloudCMA";
import { useLive } from "../LiveProvider";
import IconHamburger from "../icons/IconHamburger";
import IconHide from "../icons/IconHide";
import IconHome from "../icons/IconHome";
import IconView from "../icons/IconView";
import LiveMeetingServices from "./LiveMeetingServices";
import {
  CompanyLogo,
  Container,
  Customize,
  Feedback,
  Header,
  Logo,
  Meeting,
  Menu,
  MenuButton,
  MenuContent,
  MenuFooter,
  Module,
  ModuleAction,
  ModuleBadge,
  ModuleLink,
  ModuleLinkIcon,
  ModuleLinkText,
  Modules,
  Sparkle
} from "./styled/live-menu";
import { getClosedListings } from "../../../utils/listing";

function LiveMenu() {
  const queryParams = useQueryParams();
  const _isEditing = queryParams.get("edit") === "true";
  const [isOpen, setIsOpen] = useState(_isEditing);
  const [isEditing, setIsEditing] = useState(_isEditing);

  const handleClose = () => {
    setIsOpen(false);
    setIsEditing(false);
  };

  return (
    <Container className="menu">
      <MenuButton
        className="menu-button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}>
        <IconHamburger />
      </MenuButton>
      {isOpen && (
        <LiveMenuItems
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          onClose={handleClose}
        />
      )}
    </Container>
  );
}

function LiveMenuItems({ isEditing, setIsEditing, onClose }) {
  const containerRef = useRef();
  const { guid } = useParams();
  const { pathname } = useLocation();
  const { agent, isReportOwnedByCurrentAgent } = useLive();
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (
        e.target === containerRef.current ||
        containerRef.current.contains(e.target)
      ) {
        return;
      }
      handleClose();
    };
    window.addEventListener("click", handler);
    return () => {
      window.removeEventListener("click", handler);
    };
  }, [handleClose]);

  // Hack around mounting and animating
  useEffect(() => {
    if (!isClosing) {
      setTimeout(() => {
        containerRef.current.style.transform = `translateX(0%)`;
      });
    } else {
      containerRef.current.style.transform = `translateX(-110%)`;
    }
  }, [isClosing]);

  // Close menu if changing routes
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    if (pathnameRef.current !== pathname) {
      handleClose();
    }
  }, [pathname, handleClose]);

  return (
    <FocusLock returnFocus={true}>
      <Menu
        ref={containerRef}
        onKeyDown={(e) => e.key === "Escape" && handleClose()}
        onTransitionEnd={() => isClosing && onClose()}>
        <Header>
          <MenuButton data-autofocus onClick={handleClose}>
            <IconTimes />
          </MenuButton>
          <Link to={`/live/${guid}`} onClick={handleClose} className="logo">
            {!agent.companyLogo && (
              <Logo>
                <IconCloudCMA />
              </Logo>
            )}
            {!!agent.companyLogo && (
              <CompanyLogo src={agent.companyLogo} alt={agent.companyName} />
            )}
          </Link>
        </Header>
        <MenuContent>
          <LiveMenuModules isEditing={isEditing} />
        </MenuContent>
        {isReportOwnedByCurrentAgent && (
          <MenuFooter>
            <Customize
              className="menu-footer-customize"
              onClick={() => setIsEditing(!isEditing)}>
              {isEditing ? "Done" : "Customize"}
            </Customize>
            <Meeting>
              <LiveMeetingServices />
            </Meeting>
            <Feedback
              href="https://docs.google.com/forms/d/e/1FAIpQLSc6WjXDtCKYrbB_WLSzh9s6BAZFN-34E8DiVqkzpAKPXVS8GQ/viewform"
              target="_blank"
              rel="noopener noreferrer">
              Give feedback on Cloud CMA Live
            </Feedback>
          </MenuFooter>
        )}
      </Menu>
    </FocusLock>
  );
}

function LiveMenuModules({ isEditing }) {
  const { guid } = useParams();
  const { modules, reorderModules } = useLive();
  const visibleModules = modules.filter((module) =>
    isEditing ? true : !module.isHidden
  );

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    reorderModules(result.source.index, result.destination.index);
  };

  return (
    <Modules>
      <Module>
        <ModuleLink to={`/live/${guid}`}>
          <ModuleLinkIcon aria-hidden>
            <IconHome />
          </ModuleLinkIcon>
          <ModuleLinkText>Home</ModuleLinkText>
        </ModuleLink>
      </Module>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="modules">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {visibleModules.map((module, index) => (
                <LiveMenuModule
                  key={module.key}
                  module={module}
                  index={index}
                  isEditing={isEditing}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Modules>
  );
}

function LiveMenuModule({ module, index, isEditing }) {
  const { guid } = useParams();
  const isRoute = !!useRouteMatch(`/live/${guid}/${module.key}`);
  const { toggleModule, visibleListings } = useLive();
  const hasSubModules = !!module.subModules.length;
  const [isOpen, setIsOpen] = useState(hasSubModules && isRoute);
  const visibleSubModules = module.subModules.filter((subModule) =>
    isEditing ? true : !subModule.isHidden
  );

  const filteredVisibleListings = getClosedListings(visibleListings);

  return (
    <Draggable
      disableInteractiveElementBlocking
      draggableId={`draggable-${module.key}`}
      index={index}
      isDragDisabled={!isEditing || isOpen}>
      {(provided, snapshot) => (
        <Module
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          isEditing={isEditing}
          isHidden={module.isHidden}
          isDragging={snapshot.isDragging}>
          <ModuleLink to={module.url} title={module.title}>
            {module.iconEl && (
              <ModuleLinkIcon aria-hidden>{module.iconEl}</ModuleLinkIcon>
            )}
            <ModuleLinkText
              dangerouslySetInnerHTML={{ __html: module.title }}
            />
            {module.showSparkle && <Sparkle />}
            {!module.canHide && (
              <ModuleAction
                tabIndex="0"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsOpen(!isOpen);
                }}>
                <ModuleBadge>{filteredVisibleListings.length}</ModuleBadge>
              </ModuleAction>
            )}
            {module.canHide && isEditing && (
              <ModuleAction
                className={module.isHidden ? "module-hidden" : "module-visible"}
                data-title={module.title}
                tabIndex="0"
                isHidden={module.isHidden}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleModule(module.key);
                }}>
                {module.isHidden ? <IconHide /> : <IconView />}
              </ModuleAction>
            )}
          </ModuleLink>
          {isOpen && (
            <LiveMenuSubModules
              moduleKey={module.key}
              subModules={visibleSubModules}
              isEditing={isEditing}
            />
          )}
        </Module>
      )}
    </Draggable>
  );
}

function LiveMenuSubModules({ moduleKey, subModules, isEditing }) {
  const { reorderSubModules } = useLive();

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    reorderSubModules(moduleKey, result.source.index, result.destination.index);
  };

  return (
    <Modules>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={`droppable-${moduleKey}`}>
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {subModules.map((subModule, index) => (
                <LiveMenuSubModule
                  key={subModule.id || subModule.key}
                  moduleKey={moduleKey}
                  subModule={subModule}
                  index={index}
                  isEditing={isEditing}
                  isDragDisabled={!isEditing || subModules.length <= 1}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Modules>
  );
}

function LiveMenuSubModule({
  moduleKey,
  subModule,
  index,
  isEditing,
  isDragDisabled
}) {
  const { toggleSubModule } = useLive();

  return (
    <Draggable
      draggableId={`draggable-${subModule.key}`}
      index={index}
      isDragDisabled={isDragDisabled}>
      {(provided, snapshot) => (
        <Module
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          isEditing={isEditing}
          isHidden={subModule.isHidden}
          isDragging={snapshot.isDragging}>
          <ModuleLink to={subModule.url} title={subModule.title}>
            {subModule.iconEl && (
              <ModuleLinkIcon aria-hidden>{subModule.iconEl}</ModuleLinkIcon>
            )}
            <ModuleLinkText>{subModule.title}</ModuleLinkText>
            {isEditing && (
              <ModuleAction
                isEditing={isEditing}
                isHidden={subModule.isHidden}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleSubModule(moduleKey, subModule.key);
                }}>
                {subModule.isHidden ? <IconHide /> : <IconView />}
              </ModuleAction>
            )}
          </ModuleLink>
        </Module>
      )}
    </Draggable>
  );
}

export default LiveMenu;
